import { createSelector } from 'reselect';

// Types
import { PaymentType } from 'types/Constants.types';
import { RootState } from 'types/RootState.types';

// Config
import paymentTypeConstants from '@constants/paymentTypeConstants';
import {
  getApplyPromoCodeFailedSelector,
  getApplyPromoCodeLoadingSelector,
  getOrderFormIsClickCollectOrderSelector,
  getOrderFormPaymentsSelector,
  getOrderFormPromoCodeAppliedSelector,
  getOrderFormRemoveGiftOptionLoadingSelector,
  getOutstandingBalanceSelector,
  getPaymentPageCompleteSelector,
  getPromoCodeAppliedIsLoadingSelector,
  getRemovePromoCodeFailedSelector,
  getRewardsDiscountTotalSelector,
  getSilentlyAppliedPromoCodeSelector,
} from '@redux/reducers/order-form/orderFormSelector';
import {
  getGiftOptionsModalContentIdSelector,
  getGiftOptionsModalOpenSelector,
  getPaymentSubmitOrderErrorSelector,
  getPromoCodeError,
  getPromoCodeSubmissionErrorSelector,
} from '@redux/reducers/payment/paymentSelector';
import { getGiftCardIsLoadingSelector, giftCardsSelector } from '@redux/reducers/payment/giftCardSelector';
import { getGiftOptionWithRemainingBalance } from '@redux/reducers/payment/giftOptionsHigherOrderReducer';
import { getGiftVoucherIsLoadingSelector } from '@redux/reducers/payment/giftVoucherSelector';
import { getRouterLocationPathname } from '@redux/reducers/router/routerSelector';

const getGiftOptionWithRemainingBalanceTyped = (
  state: RootState,
  paymentType: PaymentType,
): {
  cardNumber?: string;
  remainingBalance?: string;
  serialNumber?: string;
} => {
  const adaptedState = {
    orderForm: {
      payments: (state.orderForm?.payments ?? []) as never[],
    },
  };

  return getGiftOptionWithRemainingBalance(adaptedState as { orderForm?: { payments?: never[] } }, paymentType);
};

const getGiftOptionBalancesSelector = (state: RootState) => ({
  giftCard: getGiftOptionWithRemainingBalanceTyped(state, paymentTypeConstants.GIFT_CARD),
  giftVoucher: getGiftOptionWithRemainingBalanceTyped(state, paymentTypeConstants.GIFT_VOUCHER),
});

const giftCardsListSelector = (state: RootState, renderingContext: string) =>
  giftCardsSelector(state, renderingContext);

const paymentDeductionsState = createSelector(
  [
    getApplyPromoCodeFailedSelector,
    getApplyPromoCodeLoadingSelector,
    getPaymentPageCompleteSelector,
    getOrderFormIsClickCollectOrderSelector,
    getGiftOptionsModalContentIdSelector,
    getGiftOptionsModalOpenSelector,
    getOrderFormPromoCodeAppliedSelector,
    getPromoCodeError,
    getPromoCodeSubmissionErrorSelector,
    getRewardsDiscountTotalSelector,
    getRemovePromoCodeFailedSelector,
    getSilentlyAppliedPromoCodeSelector,
    getPaymentSubmitOrderErrorSelector,
    getOrderFormRemoveGiftOptionLoadingSelector,
    getPromoCodeAppliedIsLoadingSelector,
    getOutstandingBalanceSelector,
    getGiftCardIsLoadingSelector,
    getGiftVoucherIsLoadingSelector,
    getOrderFormPaymentsSelector,
    getGiftOptionBalancesSelector,
    giftCardsListSelector,
    getRouterLocationPathname,
  ],
  (
    applyPromoCodeSubmitFailed,
    applyPromoCodeSubmitting,
    getPaymentPageComplete,
    isClickCollectOrder,
    modalContentId,
    modalOpen,
    orderFormPromoCodeApplied,
    promoCodeError,
    promoCodeSubmissionError,
    rewardApplied,
    showRemovePromoCodeErrorMessage,
    silentlyAppliedPromoCode,
    submitOrderError,
    orderFormRemoveGiftOptionLoading,
    orderFormPromoCodeAppliedIsLoading,
    orderFormOutstandingBalanceSelector,
    giftCardIsLoadingSelector,
    giftVoucherIsLoadingSelector,
    payments,
    giftOptionBalances,
    giftCardsList,
    pathname,
  ) => {
    const outstandingBalance = orderFormOutstandingBalanceSelector ?? '';
    const { usedGiftCards: giftCards = [], unusedGiftCards } = giftCardsList;
    const promoCodeApplied = orderFormPromoCodeApplied ?? {};

    const giftApplicationApiCallIsActive =
      giftCardIsLoadingSelector ||
      giftVoucherIsLoadingSelector ||
      orderFormRemoveGiftOptionLoading ||
      orderFormPromoCodeAppliedIsLoading;

    return {
      applyPromoCodeSubmitFailed,
      applyPromoCodeSubmitting,
      getPaymentPageComplete,
      giftApplicationApiCallIsActive,
      giftCards,
      giftOptionBalances,
      isClickCollectOrder,
      modalContentId: modalContentId ?? '',
      modalOpen,
      outstandingBalance,
      pathname: pathname ?? '',
      payments: payments ?? [],
      promoCodeApplied,
      promoCodeError: promoCodeError ?? '',
      promoCodeSubmissionError,
      rewardApplied: !!rewardApplied,
      showRemovePromoCodeErrorMessage,
      silentlyAppliedPromoCode,
      submitOrderError,
      unusedGiftCards,
    };
  },
);

export default paymentDeductionsState;
